import { useCallback, useEffect, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { db, refreshKeywordClusters } from "../blog/blogFirebase";

// One audit doc per run, ID = YYYY-MM (services/blog/clusters/clusterRepository.js).
// Admins have read access via firestore.rules, so the last-run panel reads the
// collection directly instead of paying for another callable round-trip.
const REFRESHES_COLLECTION = "blog_keyword_refreshes";

/** Firestore Timestamp | ISO string | Date → Date | null. */
function toDate(value) {
  if (!value) return null;
  const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Owns the keyword-refresh screen state:
 *   - live subscription to the most recent refresh audit doc
 *   - the manual trigger (dry run or real) plus its busy/error/result state
 *
 * A dry run deliberately does NOT produce an audit doc (refreshClusterMetrics
 * returns before recordRefresh), so previewing never moves "last refresh".
 */
export function useKeywordRefresh() {
  const [lastRun, setLastRun]         = useState(null);
  const [historyLoading, setLoading]  = useState(true);
  const [historyError, setHistErr]    = useState("");

  const [running, setRunning]         = useState(false);
  const [runKind, setRunKind]         = useState(null); // "preview" | "refresh"
  const [result, setResult]           = useState(null);
  const [error, setError]             = useState("");

  useEffect(() => {
    // Ordered by runAt rather than doc ID: a descending __name__ sort needs an
    // explicit composite index, while a regular field is served by Firestore's
    // automatic single-field index.
    const q = query(
      collection(db, REFRESHES_COLLECTION),
      orderBy("runAt", "desc"),
      limit(1)
    );

    const unsub = onSnapshot(q, (snap) => {
      const doc = snap.docs[0];
      // Empty is a valid state: clusters can be seeded but never refreshed.
      setLastRun(doc ? { id: doc.id, ...doc.data(), runAt: toDate(doc.data().runAt) } : null);
      setLoading(false);
      setHistErr("");
    }, (err) => {
      console.error("[useKeywordRefresh] history snapshot error:", err);
      setHistErr(err.message || "Could not read refresh history.");
      setLoading(false);
    });

    return () => unsub();
  }, []);

  const run = useCallback(async (dryRun) => {
    setRunning(true);
    setRunKind(dryRun ? "preview" : "refresh");
    setError("");
    setResult(null);
    try {
      const res = await refreshKeywordClusters({ dryRun });
      setResult(res.data?.summary || null);
    } catch (e) {
      console.error("refreshKeywordClustersNow error:", e);
      // The most common failure is a missing keys.dataForSeo credential, so the
      // message has to reach the screen rather than only the console.
      setError(e?.message || "Keyword refresh failed.");
    } finally {
      setRunning(false);
      setRunKind(null);
    }
  }, []);

  const preview = useCallback(() => run(true), [run]);
  const refresh = useCallback(() => run(false), [run]);

  const clearResult = useCallback(() => {
    setResult(null);
    setError("");
  }, []);

  return {
    lastRun, historyLoading, historyError,
    running, runKind, result, error,
    preview, refresh, clearResult,
  };
}
